import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slide from "@mui/material/Slide";
import Fade from "@mui/material/Fade";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Langs from "../../../../app/lang/langs";
import {
    editResponseManager,
    setProjectProgress,
    setProjectProgressDetails,
} from "../../vcompSlice";
import MobileNumberStepper from "./MobileNumberStepper";

const options = [
    { label: "Rien fait", value: 0 },
    { label: "Lu/Expliqué une fois (en partie)", value: 1 },
    { label: "Lu/Expliqué une fois (totalité)", value: 2 },
    { label: "Compris/Connu en partie", value: 3 },
    { label: "Compris/Connu en totalité", value: 4 },
    { label: "Maîtrisé (Autonome)", value: 5 },
];

const getProgressValue = (progress) => {
    if (progress === 100) return 5;
    if (progress === 80) return 4;
    if (progress === 60) return 3;
    if (progress === 40) return 2;
    if (progress === 20) return 1;
    return 0;
};

const getMuiProgressColor = (num) => {
    if (num < 25) return "#f44336";
    if (num < 50) return "#FF9015";
    if (num < 75) return "#FFBF00";
    return "#4caf50";
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * MobileAptitudeModal - Mobile-friendly version of AptitudeModal.
 * Full-screen dialog at zIndex 1500 showing collab progress for an aptitude.
 * Includes inline edit sub-dialog for changing a collab's progress level.
 */
export default function MobileAptitudeModal({
    show,
    handleClose,
    aptitude,
    edit,
    sessionNumber,
    maxSession,
    handleSessionNumberChange,
}) {
    const dispatch = useDispatch();
    const progressDetails = useSelector((state) => state.vcomp.projectProgressDetails);

    const [localAptitude, setLocalAptitude] = useState(aptitude);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedCollab, setSelectedCollab] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        setLocalAptitude(aptitude);
    }, [aptitude]);

    useEffect(() => {
        if (selectedCollab && selectedCollab.progress != null) {
            setSelectedOption(getProgressValue(selectedCollab.progress));
        }
    }, [selectedCollab]);

    const handleEditClick = (collab) => {
        setSelectedCollab(collab);
        setEditOpen(true);
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setSelectedCollab(null);
        setSelectedOption(null);
    };

    const handleSave = () => {
        if (selectedCollab && selectedOption !== null) {
            dispatch(
                editResponseManager({
                    id: selectedCollab.id_resp,
                    progress: selectedOption,
                })
            );
            updateLocalAptitude(selectedCollab.id_resp, selectedOption * 20);
            dispatch(
                setProjectProgressDetails(
                    recalculateProjectProgress(
                        progressDetails,
                        selectedCollab.id_resp,
                        selectedOption * 20
                    )
                )
            );
        }
        handleEditClose();
    };

    const updateLocalAptitude = (id_resp, newProgress) => {
        setLocalAptitude((prev) => ({
            ...prev,
            collabs_answers: prev.collabs_answers.map((answer) =>
                answer.id_resp === id_resp ? { ...answer, progress: newProgress } : answer
            ),
        }));
    };

    function recalculateProjectProgress(data, idResp, newProgress) {
        const dataClone = JSON.parse(JSON.stringify(data));
        function recurse(elements) {
            return elements.map((element) => {
                if (element.scope === "aptitude") {
                    const index = element.collabs_answers?.findIndex(
                        (ca) => ca.id_resp === idResp
                    );
                    if (index !== -1 && index !== undefined) {
                        element.collabs_answers[index] = {
                            ...element.collabs_answers[index],
                            progress: newProgress,
                        };
                    }
                    const collabAnswers = element.collabs_answers;
                    const collabNoAnswers = element.collabs_no_answers.length;
                    element.progress =
                        collabAnswers.reduce((acc, curr) => acc + curr.progress, 0) /
                        (collabAnswers.length + collabNoAnswers || 1);
                }
                if (element.children && element.children.length > 0) {
                    element.children = recurse(element.children);
                    const totalCoef = element.children.reduce((acc, child) => acc + child.coef, 0);
                    const weightedSum = element.children.reduce(
                        (acc, child) => acc + child.progress * child.coef,
                        0
                    );
                    const averageProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
                    element.progress = averageProgress;
                }
                return element;
            });
        }
        const categories = recurse(dataClone);
        const totalCoef = categories.reduce((acc, cat) => acc + (cat.coef || 1), 0);
        const weightedSum = categories.reduce(
            (acc, cat) => acc + (cat.progress || 0) * (cat.coef || 1),
            0
        );
        const averageProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
        dispatch(setProjectProgress(parseFloat(averageProgress).toFixed(2)));
        return categories;
    }

    return (
        <>
            {/* ── Main Modal: Collab progress list ── */}
            <Dialog
                fullScreen
                open={show}
                onClose={handleClose}
                TransitionComponent={Transition}
                sx={{ zIndex: 1500 }}
            >
                {/* Sticky AppBar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: "#fff",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                    }}
                >
                    <Toolbar sx={{ justifyContent: "space-between", minHeight: 56 }}>
                        <IconButton edge="start" onClick={handleClose} sx={{ color: "#4b5263" }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: "#111318",
                                textAlign: "center",
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                mx: 1,
                                fontSize: "0.95rem",
                            }}
                        >
                            {localAptitude?.label}
                        </Typography>
                        <IconButton edge="end" onClick={handleClose} sx={{ color: "#4b5263" }}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", bgcolor: "#fafbfc" }}>
                    {/* Section header */}
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontSize: "0.9rem" }}>
                            <Langs str="COLLAB_PROGRESS" />
                        </Typography>
                    </Box>

                    {/* Collab answers list */}
                    {localAptitude?.collabs_answers && localAptitude.collabs_answers.length > 0 ? (
                        <Box sx={{ px: 2 }}>
                            {localAptitude.collabs_answers.map((answer, index) => {
                                const progressVal = parseFloat(Number(answer.progress || 0).toFixed(1));
                                return (
                                    <Fade in key={index} timeout={300 + index * 80}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                py: 1.5,
                                                borderBottom: "1px solid rgba(0,0,0,0.06)",
                                            }}
                                        >
                                            {/* Name */}
                                            <Box sx={{ minWidth: 0, flex: "0 0 auto", maxWidth: "30%" }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "0.85rem",
                                                        color: "#333",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {answer.prenom} {answer.nom}
                                                </Typography>
                                            </Box>

                                            {/* Progress bar */}
                                            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(progressVal, 100)}
                                                    sx={{
                                                        flex: 1,
                                                        height: 8,
                                                        borderRadius: 4,
                                                        bgcolor: "rgba(0,0,0,0.06)",
                                                        "& .MuiLinearProgress-bar": {
                                                            borderRadius: 4,
                                                            bgcolor: getMuiProgressColor(progressVal),
                                                        },
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        fontWeight: 600,
                                                        color: getMuiProgressColor(progressVal),
                                                        minWidth: 36,
                                                        textAlign: "right",
                                                    }}
                                                >
                                                    {progressVal}%
                                                </Typography>
                                            </Box>

                                            {/* Edit button */}
                                            {edit && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditClick(answer)}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        color: "#FF9015",
                                                        bgcolor: "rgba(255,144,21,0.08)",
                                                        borderRadius: 2,
                                                        "&:active": { transform: "scale(0.93)" },
                                                        transition: "all 0.15s ease",
                                                    }}
                                                >
                                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Fade>
                                );
                            })}
                        </Box>
                    ) : (
                        <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#999" }}>
                                <Langs str="EMPTY_PROGRESS" />
                            </Typography>
                        </Box>
                    )}

                    {/* Collabs with no answers */}
                    {localAptitude?.collabs_no_answers && localAptitude.collabs_no_answers.length > 0 && (
                        <Box sx={{ px: 2, mt: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "#999", fontSize: "0.8rem", mb: 1, mt: 2 }}
                            >
                                <Langs str="NO_ANSWER" />
                            </Typography>
                            {localAptitude.collabs_no_answers.map((noAnswer, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        py: 1.5,
                                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#666" }}>
                                        {noAnswer.prenom} {noAnswer.nom}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#bbb", fontSize: "0.75rem" }}>
                                        <Langs str="NO_ANSWER" />
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Sticky footer: session stepper + close */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        bgcolor: "rgba(255,255,255,0.95)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        px: 2,
                        py: 2,
                        pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <MobileNumberStepper
                            label={<Langs str="Choose Session" />}
                            value={sessionNumber}
                            onChange={(e) => handleSessionNumberChange(e)}
                            min={1}
                            max={maxSession}
                            step={1}
                        />
                    </Box>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderColor: "rgba(0,0,0,0.15)",
                            color: "#555",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "14px",
                            minHeight: 48,
                            fontSize: "0.95rem",
                            "&:active": { transform: "scale(0.98)" },
                            transition: "all 0.15s ease",
                        }}
                    >
                        <Langs str="CLOSE" />
                    </Button>
                </Box>
            </Dialog>

            {/* ── Edit Sub-Modal: Progress level picker ── */}
            <Dialog
                open={editOpen}
                onClose={handleEditClose}
                TransitionComponent={Transition}
                transitionDuration={{ enter: 350, exit: 250 }}
                sx={{
                    zIndex: 1600,
                    "& .MuiBackdrop-root": {
                        backdropFilter: "blur(6px)",
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                    },
                }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: "20px",
                        mx: 2,
                        maxWidth: "sm",
                        width: "100%",
                        overflow: "hidden",
                        bgcolor: "#fff",
                        border: "1px solid rgba(0,0,0,0.08)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem", color: "#111" }}>
                        <Langs str="EDIT_PROGRESS" />
                    </Typography>
                    {selectedCollab && (
                        <Typography variant="body2" sx={{ color: "#888", fontSize: "0.85rem", mt: 0.25 }}>
                            {selectedCollab.prenom} {selectedCollab.nom}
                        </Typography>
                    )}
                </Box>

                {/* Options */}
                <Box sx={{ px: 2.5, py: 1.5 }}>
                    <RadioGroup
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(Number(e.target.value))}
                    >
                        {options.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={
                                    <Radio
                                        sx={{
                                            color: "rgba(0,0,0,0.25)",
                                            "&.Mui-checked": { color: "#FF9015" },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>
                                        {option.label}
                                    </Typography>
                                }
                                sx={{
                                    mx: 0,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 2,
                                    mb: 0.5,
                                    bgcolor:
                                        selectedOption === option.value
                                            ? "rgba(255,144,21,0.06)"
                                            : "transparent",
                                    transition: "background 0.15s ease",
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Box>

                {/* Actions */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        px: 2.5,
                        pb: 2.5,
                        pt: 1,
                    }}
                >
                    <Button
                        fullWidth
                        variant="text"
                        onClick={handleEditClose}
                        sx={{
                            color: "#777",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "14px",
                            minHeight: 48,
                            fontSize: "0.93rem",
                            bgcolor: "rgba(0,0,0,0.03)",
                            "&:active": { transform: "scale(0.98)" },
                            transition: "all 0.15s ease",
                        }}
                    >
                        <Langs str="CLOSE" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        onClick={handleSave}
                        sx={{
                            background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                            color: "#fff",
                            fontWeight: 650,
                            textTransform: "none",
                            borderRadius: "14px",
                            minHeight: 48,
                            fontSize: "0.93rem",
                            boxShadow: "0 4px 14px rgba(255, 144, 21, 0.30)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #e07400 0%, #FF9015 100%)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                transition: "all 100ms ease",
                            },
                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                        <Langs str="SAVE" />
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}
